import TagManager from "react-gtm-module";

import { IS_PROD_ENV } from "@/constants/env";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { GAEvents } from "@/constants/gtag";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { Genders } from "@/types/gender";

import { Tracker } from "./common";

const PROD_GTM_ID = "GTM-P4MFXQN";
const DEV_GTM_ID = "GTM-5KDSXQNB";

type GAEventsType = {
    currency?: string;
    clientId?: string | null;
    email?: string;
    orderId?: string | null;
    price?: string | null;
    planId?: string | null;
    planPeriod?: string | null;
    payment_system?: string | null;
    [WebEventProps.SCREEN]?: string;
    [WebEventProps.RESULT]?: unknown;
    event_id?: string;
} & Partial<Record<string, unknown>>;

class GoogleTagManager extends Tracker {
    private _eventsQueue: { action: GAEvents | WebEventTypes; value?: GAEventsType }[] = [];

    constructor() {
        super();
    }

    init() {
        if (!this._isInitialized) {
            const tagManagerArgs = {
                gtmId: IS_PROD_ENV ? PROD_GTM_ID : DEV_GTM_ID,
            };
            TagManager.initialize(tagManagerArgs);

            this._isInitialized = true;
            this.proceedEventsQueue();
        }

        if (this._isStopped) {
            this._isStopped = false;
        }
    }

    track(action: GAEvents | WebEventTypes, value?: GAEventsType) {
        if (this._isInitialized && !this._isStopped) {
            if (typeof window !== "undefined" && typeof window.gtag !== "undefined") {
                if (
                    [
                        GAEvents.Purchase,
                        GAEvents.ViewCart,
                        GAEvents.BeginCheckout,
                        GAEvents.SignUp,
                        GAEvents.AddToCart,
                    ].includes(action as GAEvents)
                ) {
                    TagManager.dataLayer({
                        dataLayer: {
                            event: action,
                            ...(value?.clientId && { clientId: value.clientId }),
                            ...(value?.orderId && { orderId: value.orderId }),
                            ...(value?.email && { email: value.email }),
                            ...(value?.currency && { currency: value.currency }),
                            ...(value?.price && { value: value.price }),
                            ...(value?.planPeriod && { planPeriod: value.planPeriod }),
                            ...(action !== GAEvents.SignUp &&
                                value?.planId && {
                                    items: [
                                        {
                                            item_id: value.planId,
                                            item_name: value.planId,
                                            price: value.price,
                                            quantity: 1,
                                            affiliation: value.payment_system,
                                            currency: value.currency,
                                        },
                                    ],
                                }),
                            ...(value?.event_id && { event_id: value.event_id }),
                        },
                    });
                    return;
                }
                if (action === GAEvents.PurchaseUpsell) {
                    TagManager.dataLayer({
                        dataLayer: {
                            event: action,
                            ...value,
                        },
                    });
                    return;
                }
                if (action === GAEvents.Gender) {
                    const gender = value?.[WebEventProps.RESULT];
                    TagManager.dataLayer({
                        dataLayer: {
                            event: action,
                            is_female: gender === Genders.Female,
                            is_male: gender === Genders.Male,
                            gender: gender as Genders,
                        },
                    });
                    return;
                } else if (
                    action === WebEventTypes.RESULT_EVENT &&
                    [`man_${QUESTIONS_IDS.AGE}`, `female_${QUESTIONS_IDS.AGE}`].includes(
                        value?.screen ?? ""
                    )
                ) {
                    TagManager.dataLayer({
                        dataLayer: {
                            age_range: value?.result,
                        },
                    });
                }

                const currency = value?.currency ? { currency: value?.currency } : {};

                TagManager.dataLayer({
                    dataLayer: {
                        event: action,
                        question: value?.screen,
                        answer: value?.result,
                        ...currency,
                    },
                });
                return;
            }
        } else {
            this._eventsQueue.push({ action, value });
        }
    }

    private proceedEventsQueue() {
        this._eventsQueue.forEach(({ action, value }) => this.track(action, value));
    }
}

export default GoogleTagManager;
